import { useEffect, useInsertionEffect, useRef, type RefObject } from 'react';

let bodyLockCount = 0;
let lockedBody: HTMLElement | null = null;
let originalBodyOverflow = '';

/**
 * Mirror the modal lock in `body.style.overflow` for legacy consumers and the
 * characterized public contract. Radix owns wheel/touch isolation and scrollbar
 * compensation; this refcount keeps the legacy inline state correct through
 * stacked, out-of-order, and React StrictMode lifecycles.
 */
export function useScrollLock(enabled = true): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const body = document.body;
    if (bodyLockCount === 0) {
      lockedBody = body;
      originalBodyOverflow = body.style.overflow;
    }
    bodyLockCount += 1;
    body.style.overflow = 'hidden';

    return () => {
      bodyLockCount = Math.max(0, bodyLockCount - 1);
      if (bodyLockCount === 0 && lockedBody) {
        lockedBody.style.overflow = originalBodyOverflow;
        lockedBody = null;
        originalBodyOverflow = '';
      }
    };
  }, [enabled]);
}

export const LW_OVERLAY_LAYER_ATTRIBUTE = 'data-lw-overlay-layer';

/**
 * Compatibility for characterization callers that dispatch Escape directly on
 * `window`. Radix owns all real focused-element/document Escape events. For a
 * window-targeted event only, portal DOM order is the source of truth so a
 * simultaneously mounted child closes before its parent.
 */
export function useWindowEscapeFallback<T extends HTMLElement>(
  contentRef: RefObject<T | null>,
  onClose: () => void
): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.target !== event.currentTarget) {
        return;
      }

      const connectedLayers = Array.from(
        document.querySelectorAll<HTMLElement>(`[${LW_OVERLAY_LAYER_ATTRIBUTE}]`)
      ).filter((layer) => layer.isConnected);
      const topmostLayer = connectedLayers[connectedLayers.length - 1];
      if (!topmostLayer || topmostLayer !== contentRef.current) {
        return;
      }

      event.preventDefault();
      onClose();
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [contentRef, onClose]);
}

// Radix Toast is the one Radix layer that dismisses on Escape without
// preventing its default; its root carries these attributes whatever element
// renders it. Sonner toasts carry no data-state.
const OPEN_RADIX_TOAST = '[data-swipe-direction][data-state="open"]:not([data-sonner-toast])';

/**
 * Escape for a Radix dialog whose own dismissal has not caught up. Radix
 * learns a layer's position on a render after the layer registers and adopts
 * it in its Escape handler through a later effect, so an Escape pressed just
 * after the dialog opens, or just after the menu that opened it unmounts, can
 * reach no layer. This listener runs after Radix's capture listener and acts
 * only on an Escape whose default nothing prevented (Radix layers prevent it
 * when they dismiss), outside IME composition, from focus inside this dialog,
 * while no popup the dialog owns (such as a Select that is open or still
 * opening) is expanded, and when no Radix toast was open as the Escape began:
 * a window capture listener notes that before any toast can dismiss, even one
 * that unmounts synchronously. `requestClose` must be the dialog's own close
 * handler, guards included, so this path closes exactly as Radix's dismissal
 * would; like Radix's onOpenChange, the latest committed handler is called.
 */
export function useDialogEscapeFallback<T extends HTMLElement>(
  contentRef: RefObject<T | null>,
  requestClose: () => void
): void {
  const latestRequestClose = useRef(requestClose);
  useInsertionEffect(() => {
    latestRequestClose.current = requestClose;
  });

  useEffect(() => {
    const beganWithToast = new WeakSet<Event>();
    const noteToast = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && document.querySelector(OPEN_RADIX_TOAST)) {
        beganWithToast.add(event);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      const content = contentRef.current;
      if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing || !content?.isConnected) {
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[role="dialog"], [role="alertdialog"]') !== content
        || content.querySelector('[aria-expanded="true"]')
        || beganWithToast.has(event)
        || document.querySelector(OPEN_RADIX_TOAST)) {
        return;
      }

      event.preventDefault();
      latestRequestClose.current();
    };

    window.addEventListener('keydown', noteToast, true);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', noteToast, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [contentRef]);
}
